import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  prequalDocumentsApi,
  type PrequalDocumentBlockRef,
} from "@/api/prequalificationDocuments";
import { prequalDocumentKeys } from "./prequalKeys";

/** All customer-facing documents + block state + version history, scoped to a
 *  document set (`null` = Phoenix Default). */
export function usePrequalDocuments(partnerBrandId: string | null = null) {
  return useQuery({
    queryKey: prequalDocumentKeys.list(partnerBrandId),
    queryFn: () => prequalDocumentsApi.list(partnerBrandId),
  });
}

/** The selectable document sets (Phoenix Default + each active partner brand). */
export function useDocumentSets() {
  return useQuery({
    queryKey: prequalDocumentKeys.sets(),
    queryFn: () => prequalDocumentsApi.sets(),
  });
}

/** Save a block's draft edit. On a 400 the caller reads the block-tagged
 *  reasons off `ApiError.details`. Invalidates the editor reads. */
export function useSavePrequalDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      documentKey: string;
      blockKey: string;
      variant: string;
      text: string;
      partnerBrandId?: string | null;
    }) =>
      prequalDocumentsApi.saveDraft(
        vars.documentKey,
        vars.blockKey,
        vars.variant,
        vars.text,
        vars.partnerBrandId,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: prequalDocumentKeys.all });
    },
  });
}

/** Publish the document's drafted blocks. */
export function usePublishPrequalDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      documentKey: string;
      blocks?: PrequalDocumentBlockRef[];
      overrideWarnings?: boolean;
      partnerBrandId?: string | null;
    }) =>
      prequalDocumentsApi.publish(
        vars.documentKey,
        vars.blocks,
        vars.overrideWarnings,
        vars.partnerBrandId,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: prequalDocumentKeys.all });
    },
  });
}

/** Roll a document back to a prior published version. */
export function useRestorePrequalDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { documentKey: string; versionId: string; partnerBrandId?: string | null }) =>
      prequalDocumentsApi.restore(vars.documentKey, vars.versionId, vars.partnerBrandId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: prequalDocumentKeys.all });
    },
  });
}

/** Reset one block to its code default. */
export function useResetPrequalBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      documentKey: string;
      blockKey: string;
      variant: string;
      partnerBrandId?: string | null;
    }) =>
      prequalDocumentsApi.resetBlock(
        vars.documentKey,
        vars.blockKey,
        vars.variant,
        vars.partnerBrandId,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: prequalDocumentKeys.all });
    },
  });
}
